import { type JSX, useId, type ComponentProps, type ReactNode } from 'react'

import { ReadOnlyValue, useReadOnly } from '../components/read-only.tsx'
import { Label } from '../components/ui/label.tsx'
import { cn } from '../lib/cn.ts'
import { Icon } from './icon.tsx'

/** One choice in a `Select`. */
export interface SelectOption {
  value: string
  label: string
  /**
   * Draws the choice inside an `optgroup` of this name.
   *
   * Choices that share a group stay in one group when they sit together.
   * A choice with no group stays a plain `option`, so existing lists are unchanged.
   */
  group?: string
}

export type SelectProps = Omit<ComponentProps<'select'>, 'children'> & {
  'data-testid': string
  label: ReactNode
  options: readonly SelectOption[]
  /** Adds an empty first choice with this text. Absent means the list is the whole choice. */
  placeholder?: string
  /** A message means invalid; it also sets aria-invalid. */
  error?: string
  hint?: ReactNode
}

/**
 * Select — a labelled choice from a fixed list.
 *
 * A native `select`, not a Radix combobox. The reasons live in
 * `docs/decisions/0006-the-console-select-is-native.md`. Shaped like `Input`
 * on purpose — same id handling, same single message slot where the error
 * wins, same required `data-testid`. Two controls that look alike on the page
 * should not be two different props contracts in the source.
 *
 * @param props - Select props.
 * @param props.label - Field label, rendered above the control.
 * @param props.options - The choices, in the order they are shown. A `group` draws an `optgroup`.
 * @param props.placeholder - Text for an empty leading choice.
 * @param props.error - Error message; sets aria-invalid.
 * @param props.hint - Shown when there is no error.
 * @param props.id - Overrides the generated id.
 * @returns The labelled select, or the current choice as text on a read-only screen.
 */
/**
 * The native select, drawn as the pickers draw their trigger.
 *
 * The merchant and reason pickers are outline buttons with a chevron, and a
 * native select beside them drew white with the browser's own arrow. This
 * keeps the native element, for its keyboard and its phone wheel, and draws
 * it with the same fill, edge and chevron.
 */
export const CHOOSER_CLASS = cn(
  'border-input bg-card h-9 w-full appearance-none rounded-none border-(length:--hairline-width) pr-8 pl-3 text-sm shadow-none',
  'hover:bg-accent hover:text-accent-foreground',
  'focus-visible:ring-0',
  'disabled:cursor-not-allowed disabled:bg-sunk disabled:text-muted-foreground'
)

/**
 * The chevron over a chooser's right end. The select under it still takes
 * the click, because the glyph passes pointer events through.
 *
 * @returns The chevron.
 */
export function ChooserChevron(): JSX.Element {
  return (
    <Icon
      name="chevrons-up-down"
      className="pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2 text-muted-foreground"
    />
  )
}

/**
 * The words a read-only select shows for its current choice.
 *
 * @param options - The choices.
 * @param value - The value in force.
 * @param placeholder - The text for an empty choice.
 * @returns The label to print.
 */
/**
 * The choices as `option` elements, inside an `optgroup` when a choice names a group.
 *
 * A run of the same group is one `optgroup`. A choice with no group stays a
 * direct `option`, including one that sits between groups. Callers that pass
 * no group get the same flat list they had before.
 *
 * @param options - The choices, in order.
 * @returns The option elements.
 */
function selectOptions(options: readonly SelectOption[]): ReactNode {
  const usesGroups = options.some(option => option.group)
  if (!usesGroups) {
    return options.map(option => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))
  }

  const nodes: ReactNode[] = []
  let index = 0
  while (index < options.length) {
    const group = options[index]?.group
    if (!group) {
      const option = options[index]
      if (option) {
        nodes.push(
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        )
      }
      index += 1
      continue
    }

    const start = index
    const bucket: SelectOption[] = []
    while (index < options.length && options[index]?.group === group) {
      const option = options[index]
      if (option) bucket.push(option)
      index += 1
    }
    nodes.push(
      <optgroup key={`${group}-${start}`} label={group}>
        {bucket.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </optgroup>
    )
  }

  return nodes
}

function selectedLabel(
  options: readonly SelectOption[],
  value: SelectProps['value'],
  placeholder: string | undefined
): string {
  const text = value === undefined || value === null ? '' : String(value)
  const match = options.find(option => option.value === text)
  if (match !== undefined) return match.label
  if (text === '') return placeholder ?? ''

  return text
}

export function Select({
  label,
  options,
  placeholder,
  error,
  hint,
  id,
  className,
  'data-testid': testId,
  value,
  ...props
}: SelectProps): JSX.Element {
  const readOnly = useReadOnly()
  const generated = useId()
  if (readOnly) {
    return (
      <ReadOnlyValue
        testId={testId}
        label={label}
        value={selectedLabel(options, value, placeholder)}
      />
    )
  }
  const fieldId = id ?? generated
  const messageId = `${fieldId}-message`
  const message = error ?? hint

  return (
    <div className="grid gap-2">
      <Label htmlFor={fieldId} className="[&&]:type-eyebrow">
        {label}
      </Label>
      <div className="relative">
        <select
          id={fieldId}
          data-testid={testId}
          aria-invalid={error ? true : undefined}
          aria-describedby={message ? messageId : undefined}
          className={cn(
            CHOOSER_CLASS,
            error && 'border-destructive',
            className
          )}
          value={value}
          {...props}
        >
          {placeholder === undefined ? null : (
            <option value="">{placeholder}</option>
          )}
          {selectOptions(options)}
        </select>
        <ChooserChevron />
      </div>
      {message ? (
        <p
          id={messageId}
          className={cn(
            'text-sm',
            error ? 'text-destructive' : 'text-muted-foreground'
          )}
        >
          {message}
        </p>
      ) : null}
    </div>
  )
}
